// Learn's own copy: the foundations page, the "I'm stuck" picker and the sources.
// The reasons and strategies themselves live in theories.js; their write-ups in writeups.js.
import { WRITEUPS } from "./writeups";

// The pain/pleasure × present/future matrix (reference doc, Foundational Mechanism 1), laid out
// as Craig asked: present on the top row, future below, pain on the left, pleasure on the right.
// `feels` lists what each force feels like; `text` says how hard it pulls.
export const MATRIX_WHEN = ["Present", "Future"];
export const MATRIX_FEEL = ["Pain", "Pleasure"];
export const MATRIX = [
  { id: "presentPain",     when: "Present", feel: "Pain",     hue: "red",   title: "Present pain",     feels: "Effort, discomfort, boredom, anxiety.",       text: "This is the strongest force. It can push you into action, or away from the task." },
  { id: "presentPleasure", when: "Present", feel: "Pleasure", hue: "teal",  title: "Present pleasure", feels: "Relaxation, entertainment, comfort, relief.",  text: "It's what wins when you put things off." },
  { id: "futurePain",      when: "Future",  feel: "Pain",     hue: "amber", title: "Future pain",      feels: "Regret, consequences, missed opportunities.", text: "It grows as a deadline gets close." },
  { id: "futurePleasure",  when: "Future",  feel: "Pleasure", hue: "blue",  title: "Future pleasure",  feels: "Achievement, pride, freedom, satisfaction.",  text: "This is the weakest pull. It barely feels real." },
];

// "The present has an unfair advantage": what each force says when you should exercise
// but you're on the couch.
export const COUCH = {
  presentPain: "Exercising will take effort, and it'll be uncomfortable.",
  presentPleasure: "Staying on the couch feels great.",
  futurePain: "You'll be disappointed in yourself, and less healthy.",
  futurePleasure: "You'll be fitter, healthier and proud of yourself.",
};

// The motivation-action cycle (reference doc, Foundational Mechanism 2).
export const CYCLE = [
  { title: "A spark.", text: "You only need enough motivation to think \"maybe I could try one thing\"." },
  { title: "A first step.", text: "You make it so small your brain can't argue with it." },
  { title: "Momentum.", text: "Doing something lifts your mood, and that makes the next step easier." },
];

// "I'm stuck right now": each feeling opens its own page. `reasons` is the one judgement
// call: the reasons most likely behind the feeling, likeliest first (the reference doc
// maps reasons to strategies, not feelings to reasons). The strategies are derived from
// the doc's cross-reference (strategiesForReasons in theories.js), never listed here.
// hideToday drops "Go to Today" where it would contradict the feeling (someone who can't
// decide, or is overwhelmed, doesn't know what to work on yet).
export const FEELINGS = [
  { id: "cantMakeMyself", label: "I know what I need to do, but I don't do it", reasons: [1, 10, 4, 6, 20] },
  { id: "decide", label: "I can't decide what to do", reasons: [12, 13, 11, 8, 16], hideToday: true },
  { id: "overwhelmed", label: "I'm feeling overwhelmed with dreaded tasks", reasons: [13, 12, 20, 17, 1], hideToday: true },
  { id: "ratherBeDoing", label: "There are so many things I'd rather be doing", reasons: [9, 3, 4, 15, 16] },
  { id: "whyKeep", label: "Why do I keep procrastinating?", reasons: [1, 14, 17, 6, 19] },
  { id: "tired", label: "I'm tired and don't feel like doing anything", reasons: [20, 18, 19, 1] },
  { id: "canWait", label: "It's not due for a while, so it can wait", reasons: [16, 15, 21, 2, 6] },
  { id: "anxious", label: "I feel anxious about things I keep putting off", reasons: [1, 17, 13, 5] },
];

export const findFeeling = (id) => FEELINGS.find(f => f.id === id) || null;

// What Chat receives from "Talk it through" at the bottom of a feeling's page.
export const feelingTalkMessage = (feeling) =>
  `${feeling.label}${feeling.label.endsWith("?") ? "" : "."} Help me figure out how to take action.`;

// Sources behind "How motivation works" (reference doc, Foundational Mechanisms).
export const FOUNDATION_SOURCES = [
  "Kahneman, D. & Tversky, A. (1979). Prospect theory: An analysis of decision under risk. Econometrica.",
  "Ainslie, G. (1975). Specious reward: A behavioral theory of impulsiveness and impulse control. Psychological Bulletin.",
  "Tice, D.M. & Baumeister, R.F. (1997). Longitudinal study of procrastination, performance, stress, and health. Psychological Science.",
  "Sirois, F. & Pychyl, T. (2013). Procrastination and the priority of short-term mood regulation. Social and Personality Psychology Compass.",
  "Steel, P. (2007). The nature of procrastination. Psychological Bulletin.",
  "Lewinsohn, P.M. (1974). A behavioral approach to depression. In R.J. Friedman & M.M. Katz (Eds.), Psychology of depression. Winston.",
  "Fredrickson, B.L. & Joiner, T. (2002). Positive emotions trigger upward spirals toward emotional well-being. Psychological Science.",
  "Kruglanski, A.W., Chernikova, M. & Schori-Eyal, N. (2014). The concept of motivational readiness. Polish Psychological Bulletin.",
];

// Popular books among the sources, shown first under a plain label. `cite` is
// how the write-ups cite the book, so it is listed once, here.
const BOOKS = [
  { cite: "Clear, J. (2018)", label: "James Clear — Atomic Habits (2018)" },
  { cite: "Thaler, R.H. & Sunstein, C.R. (2008)", label: "Richard Thaler & Cass Sunstein — Nudge (2008)" },
  { cite: "Allen, D. (2001)", label: "David Allen — Getting Things Done (2001)" },
  { cite: "Dweck, C.S. (2006)", label: "Carol Dweck — Mindset (2006)" },
  { cite: "Tracy, B. (2001)", label: "Brian Tracy — Eat That Frog! (2001)" },
  { cite: "Pressfield, S. (2002)", label: "Steven Pressfield — The War of Art (2002)" },
  { cite: "Fiore, N. (1989)", label: "Neil Fiore — The Now Habit (1989)" },
  { cite: "Amabile, T. & Kramer, S. (2011). The Progress Principle", label: "Teresa Amabile & Steven Kramer — The Progress Principle (2011)" },
  { cite: "Perry, J. (2012)", label: "John Perry — The Art of Procrastination (2012)" },
];
const isBook = (citation) => BOOKS.some(b => citation.startsWith(b.cite));

const citedBy = (prefix) => Object.entries(WRITEUPS)
  .filter(([key]) => key.startsWith(prefix))
  .flatMap(([, w]) => w.research);

// Every source Learn cites, once each: books first, then the research behind each part.
export const buildResearch = () => {
  const seen = new Set();
  const fresh = (list) => list.filter(c => !isBook(c) && !seen.has(c) && seen.add(c));
  return [
    { label: "Books you may know", items: BOOKS.map(b => b.label) },
    { label: "How motivation works", items: fresh(FOUNDATION_SOURCES) },
    { label: "Why we put things off", items: fresh(citedBy("p")).sort() },
    { label: "Conquer procrastination", items: fresh(citedBy("s")).sort() },
  ].filter(g => g.items.length);
};
export const RESEARCH = buildResearch();
export const citationOnPage = (citation) =>
  isBook(citation) || RESEARCH.some(g => g.items.includes(citation));
